import { createSlice } from '@reduxjs/toolkit';

import { CallPageStatus } from 'features/call/call-ui-state/enums';

export interface CallUiManagerState {
  state: CallPageStatus;
  isTakingCall: boolean;
  errors: string[];
}

export const initialCallUiManagerState: CallUiManagerState = {
  state: CallPageStatus.IDLE,
  isTakingCall: false,
  errors: [],
};

const callUiManagerSlice = createSlice({
  name: 'callUiManager',
  initialState: initialCallUiManagerState,
  reducers: {
    // errors are going to be permissive for now
    callReceived(state: CallUiManagerState) {
      if (
        state.state !== CallPageStatus.IDLE &&
        state.state !== CallPageStatus.SURVEY
      ) {
        const error = `Invalid state transition: ${state.state} cannot transition to CALL_RECEIVED`;
        state.errors.push(error);
      }
      state.state = CallPageStatus.CALL_RECEIVED;
    },
    connectCall(state: CallUiManagerState) {
      if (state.state === CallPageStatus.CONNECTED) {
        return;
      }
      if (
        state.state !== CallPageStatus.CALL_RECEIVED &&
        state.state !== CallPageStatus.TEAMING_OFFER_ACCEPTED &&
        state.state !== CallPageStatus.COLD_HANDOFF_OFFER_ACCEPTED
      ) {
        const error = `Invalid state transition: ${state.state} cannot transition to CONNECTED`;
        console.error(error);
        state.errors.push(error);
      }
      state.state = CallPageStatus.CONNECTED;
      state.isTakingCall = false;
    },
    acceptTeamingOffer(state: CallUiManagerState) {
      if (state.state !== CallPageStatus.CALL_RECEIVED) {
        const error = `Invalid state transition: ${state.state} cannot transition to TEAMING_AWAIT_CONNECTION`;
        state.errors.push(error);
      }
      state.state = CallPageStatus.TEAMING_OFFER_ACCEPTED;
      state.isTakingCall = false;
    },
    acceptColdHandoffOffer(state: CallUiManagerState) {
      if (state.state !== CallPageStatus.CALL_RECEIVED) {
        const error = `Invalid state transition: ${state.state} cannot transition to HANDOFF_AWAIT_CONNECTION`;
        state.errors.push(error);
      }
      state.state = CallPageStatus.COLD_HANDOFF_OFFER_ACCEPTED;
      state.isTakingCall = false;
    },
    startSurvey(state: CallUiManagerState) {
      // TODO: Check if can get to survey any other state
      if (state.state !== CallPageStatus.CONNECTED) {
        const error = `Invalid state transition: ${state.state} cannot transition to SURVEY_STAGE`;
        state.errors.push(error);
      }
      state.state = CallPageStatus.SURVEY;
      state.isTakingCall = false;
    },
    startPreviousCallSurvey(state: CallUiManagerState) {
      state.state = CallPageStatus.SURVEY;
      state.isTakingCall = false;
    },
    /** Canceled by VI1 */
    teamingCanceled(state: CallUiManagerState) {
      if (
        state.state !== CallPageStatus.TEAMING_OFFER_ACCEPTED &&
        state.state !== CallPageStatus.IDLE
      ) {
        const error = `Invalid state transition: ${state.state} cannot transition to TEAMING_OFFER_CANCELED`;
        state.errors.push(error);
      }
      state.state = CallPageStatus.TEAMING_OFFER_CANCELED;
      state.isTakingCall = false;
    },
    callTransferFailed(state: CallUiManagerState) {
      if (state.state !== CallPageStatus.COLD_HANDOFF_OFFER_ACCEPTED) {
        const error = `Invalid state transition: ${state.state} cannot transition to CALL_TRANSFER_FAILED`;
        state.errors.push(error);
      }
      state.state = CallPageStatus.CALL_TRANSFER_FAILED;
      state.isTakingCall = false;
    },
    resetToIdle(state: CallUiManagerState) {
      state.state = CallPageStatus.IDLE;
      state.isTakingCall = false;
      state.errors = [];
    },
    readyForNextCall(state: CallUiManagerState) {
      state.state = CallPageStatus.IDLE;
      state.isTakingCall = false;
      state.errors = [];
    },
    clearUiManagerErrors(state: CallUiManagerState) {
      state.errors = [];
    },
  },
  extraReducers(builder) {
    // Since there are many types of calls,
    // there are many different ways to "take" a call.

    // Sigh... thanks to our over-mocking test suite,
    // these can't just import and use the action creators they match.
    builder.addCase('callerSession/incomingCallAccepted', (state) => {
      state.isTakingCall = true;
    });
    builder.addCase('call/take/pending', (state) => {
      state.isTakingCall = true;
    });
    builder.addCase('teaming/takeRemote/pending', (state) => {
      state.isTakingCall = true;
    });
    builder.addCase('cold-handoff/take/pending', (state) => {
      state.isTakingCall = true;
    });
  },
});

export const {
  callReceived,
  connectCall,
  acceptTeamingOffer,
  acceptColdHandoffOffer,
  startSurvey,
  startPreviousCallSurvey,
  teamingCanceled,
  callTransferFailed,
  resetToIdle,
  readyForNextCall,
  clearUiManagerErrors,
} = callUiManagerSlice.actions;

export const callUiManagerReducer = callUiManagerSlice.reducer;
